
import { createContext, useContext, useState } from "react";

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  chef_id: string;
  chef: string;
  image: string;
  scheduledDate?: string;
  scheduledTime?: string;
  deliveryFee: number;
  delivery_available: boolean;
  pickupInstructions?: string;
  preparation_type: 'scheduled' | 'on_demand';
  prep_time_hours?: string;
  cutoff_time?: string;
  pickup_window_start?: string;
  pickup_window_end?: string;
  available_days?: string[];
  max_daily_orders?: string;
  availability_slots?: Array<{
    dateRange: {
      from: string;
      to: string;
    };
    recurring?: {
      days: string[];
      until: string;
    };
    startTime: string;
    endTime: string;
    availablePortions?: number;
  }>;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  updateScheduledTime: (itemId: string, date: string, time: string) => void;
  clearCart: () => void;
  total: number;
  deliveryTotal: number;
  processingFee: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>([]);

  const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const deliveryTotal = items.reduce((sum, item) => sum + item.deliveryFee, 0);
  
  const calculateProcessingFee = (amount: number): number => {
    const fee = amount * 0.029 + 0.30;
    return Math.round(fee * 100) / 100;
  };
  
  const processingFee = calculateProcessingFee(total);

  const addItem = (newItem: CartItem) => {
    const availability_slots = newItem.availability_slots ? 
      JSON.parse(JSON.stringify(newItem.availability_slots)) : [];
    
    console.log('Adding item to cart:', {
      id: newItem.id,
      name: newItem.name,
      image: newItem.image
    });

    if (!newItem.image) {
      console.warn('Item has no image URL:', newItem.name);
    }

    setItems(currentItems => {
      const existingItemIndex = currentItems.findIndex(item => 
        item.id === newItem.id && item.scheduledDate === newItem.scheduledDate
      );

      if (existingItemIndex !== -1) {
        const updatedItems = [...currentItems];
        updatedItems[existingItemIndex] = {
          ...currentItems[existingItemIndex],
          quantity: currentItems[existingItemIndex].quantity + (newItem.quantity || 1)
        };
        return updatedItems;
      }

      let initialItem = { ...newItem };
      if (initialItem.preparation_type === 'on_demand' && 
          initialItem.pickup_window_start && 
          initialItem.pickup_window_end && 
          !initialItem.scheduledTime) {
        initialItem.scheduledTime = `${initialItem.pickup_window_start} - ${initialItem.pickup_window_end}`;
      }

      // Ensure delivery_available has a default value if not provided
      if (initialItem.delivery_available === undefined) {
        initialItem.delivery_available = false;
      }

      return [...currentItems, {
        ...initialItem,
        quantity: initialItem.quantity || 1,
        availability_slots
      }];
    });
  };

  const removeItem = (itemId: string) => {
    setItems(currentItems => currentItems.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity === 0) {
      removeItem(itemId);
      return;
    }
    setItems(currentItems =>
      currentItems.map(item =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  const updateScheduledTime = (itemId: string, date: string, time: string) => {
    console.log('CartContext: Updating scheduled time:', { itemId, date, time });
    setItems(currentItems => {
      const newItems = currentItems.map(item => {
        if (item.id === itemId) {
          console.log('Updating item scheduling:', { 
            id: item.id, 
            preparation_type: item.preparation_type,
            date, 
            time,
            pickup_window_start: item.pickup_window_start,
            pickup_window_end: item.pickup_window_end
          });
          
          if (item.preparation_type === 'on_demand') {
            let timeToUse = time;
            
            if (!time || !time.includes('-')) {
              if (item.pickup_window_start && item.pickup_window_end) {
                timeToUse = `${item.pickup_window_start} - ${item.pickup_window_end}`;
                console.log('Using pickup window as time instead:', timeToUse);
              }
            }
            
            return {
              ...item,
              scheduledDate: date,
              scheduledTime: timeToUse
            };
          }
          
          return {
            ...item,
            scheduledDate: date,
            scheduledTime: time
          };
        }
        return item;
      });
      console.log('CartContext: Updated items:', newItems);
      return newItems;
    });
  };

  const clearCart = () => {
    setItems([]);
  };

  const contextValue = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    updateScheduledTime,
    clearCart,
    total,
    deliveryTotal,
    processingFee
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
